import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import '@cartwhl/ui/atoms/Dropdown.css';
import Text from '@cartwhl/ui/base/TextNew';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';

import type { AssetLibraryMediaSelection } from '../assetLibraryMediaSelection';
import { getOpenWithAppMeta } from '../assetLibraryOpenWith';
import { useAssetLibraryOpenWithActions } from '../hooks/useAssetLibraryOpenWithActions';

const MENU_CONTENT_CLASS =
  'RxDropdownContent shadow-menu z-10 w-[260px] max-w-[260px] rounded-2xl bg-white p-2 text-gray-90 dark:bg-gray-75 dark:text-white';

const OPEN_WITH_THUMBNAIL_CLASS =
  'h-14 w-[calc(3.5rem*0.65)] shrink-0 overflow-hidden rounded-lg object-cover object-center';

const MENU_ITEM_CLASS =
  'hover:bg-gray-15 dark:hover:bg-gray-90 w-full max-w-full cursor-pointer rounded-lg whitespace-normal outline-hidden transition-colors';

const OPEN_WITH_TRIGGER_CLASS =
  'bg-blue text-white inline-flex h-10 cursor-pointer items-center justify-center gap-2 rounded-lg pr-3 pl-4 text-[14px]/[14px] font-medium transition-all duration-200 ease-in-out hover:brightness-110';

function OpenWithTriggerChevron({ open }: { open: boolean }) {
  return (
    <ChevronDropdownIcon
      className={`h-5 w-5 shrink-0 transition-transform ${open ? '' : 'rotate-180'}`}
    />
  );
}

export interface AssetLibraryMediaPreviewOpenWithButtonProps {
  mediaSelection: AssetLibraryMediaSelection;
  onNavigate: () => void;
}

function OpenWithAppVideoThumbnail({
  src,
  hovered,
}: {
  src: string;
  hovered: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: inViewRef, inView } = useInView({
    rootMargin: '200px',
    threshold: 0.1,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !inView) return;
    if (hovered) {
      void video.play();
    } else {
      video.pause();
    }
  }, [hovered, inView]);

  return (
    <div ref={inViewRef} className={OPEN_WITH_THUMBNAIL_CLASS}>
      {inView ? (
        <video
          ref={videoRef}
          src={src}
          className="h-full w-full rounded-[inherit] object-cover"
          muted
          playsInline
          loop
          disablePictureInPicture
        />
      ) : null}
    </div>
  );
}

function OpenWithAppThumbnail({
  src,
  hovered = false,
}: {
  src: string;
  hovered?: boolean;
}) {
  if (src.endsWith('.webm') || src.endsWith('.mp4')) {
    return <OpenWithAppVideoThumbnail src={src} hovered={hovered} />;
  }

  return <img src={src} alt="" className={OPEN_WITH_THUMBNAIL_CLASS} />;
}

function OpenWithAppRow({
  label,
  subtitle,
  imageSrc,
}: {
  label: string;
  subtitle: string;
  imageSrc: string;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="grid w-full max-w-full grid-cols-[calc(3.5rem*0.65)_minmax(0,1fr)] items-start gap-x-3 px-3 py-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <OpenWithAppThumbnail src={imageSrc} hovered={hovered} />
      <div className="flex min-w-0 flex-col gap-2 self-start">
        <Text
          as="div"
          token="14/medium"
          leading="title"
          className="text-primary pt-1"
        >
          {label}
        </Text>
        <Text as="div" token="14/medium" className="text-tertiary break-words">
          {subtitle}
        </Text>
      </div>
    </div>
  );
}

function VideoOpenWithMenu({
  mediaSelection,
  onNavigate,
}: {
  mediaSelection: AssetLibraryMediaSelection;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { openComicWithVideo } = useAssetLibraryOpenWithActions();
  const comicMeta = getOpenWithAppMeta('comic');

  const handleSelect = (action: () => void) => {
    action();
    setOpen(false);
    onNavigate();
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenu.Trigger asChild>
        <button type="button" className={OPEN_WITH_TRIGGER_CLASS}>
          <span className="truncate pt-[0.15em]">Open with</span>
          <OpenWithTriggerChevron open={open} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={MENU_CONTENT_CLASS}
        align="end"
        side="top"
        sideOffset={8}
        collisionPadding={8}
      >
        <DropdownMenu.Item
          className={MENU_ITEM_CLASS}
          onSelect={() => {
            handleSelect(() => openComicWithVideo(mediaSelection));
          }}
        >
          <OpenWithAppRow
            label={comicMeta.label}
            subtitle={comicMeta.subtitle}
            imageSrc={comicMeta.imageSrc}
          />
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default function AssetLibraryMediaPreviewOpenWithButton({
  mediaSelection,
  onNavigate,
}: AssetLibraryMediaPreviewOpenWithButtonProps) {
  if (mediaSelection.kind !== 'media-video') return null;

  return (
    <div className="relative flex shrink-0 justify-end">
      <VideoOpenWithMenu
        mediaSelection={mediaSelection}
        onNavigate={onNavigate}
      />
    </div>
  );
}
